import without from 'lodash/without';
import find from 'lodash/find';
import omit from 'lodash/omit';
import intersection from 'lodash/intersection';
import union from 'lodash/union';
import map from 'lodash/map';
import get from 'lodash/get';
import includes from 'lodash/includes';

import moment from 'moment-timezone';
import store from '@Store';
import { Map, fromJS } from 'immutable';
import { isAnonymousUser } from '@Services/Auth/AuthHelper';
import UserDatabases from '@Services/UserDatabases';
import Catalyst from '@Services/Catalyst';

import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import { email as userEmailSelector } from '@Containers/Header/HeaderComponentSelectors';
import { resetAlerts } from '@Pages/Alerts/AlertsAction';
import { typeOfAlertCreateSuccessDialog } from '@Pages/Alerts/AlertsComponents/AlertCreateSuccessDialog/AlertCreateSuccessDialogConstants';
import {
  getAlertsInfo as getAlertsInfoService,
  importAlertsWhereClause as importAlertsWhereClauseService,
  createAlert as createAlertService,
  updateAlert as updateAlertService,
} from '@Pages/Alerts/AlertsService';
import extend from 'lodash/extend';
import pick from 'lodash/pick';
import { defineAlertDescriptionFromFactItem, defineAlertStructuresFromFactItem } from './queryUtils';
import { defineAlertDetails, defineAlertFrequency } from './trackingUtils';

// dialogs

import {
  typeOfAlertCreateDialog,
  alertCreateDialogMergeStateReducerType,
  alertCreateDialogResetReducerType,
  alertCreateDialogMergeFormReducerType,
  alertCreateDialogUpdateFormReducerType,
  typeOfAlertLoginDialog,
  alertDialogModes,
} from './AlertCreateDialogConstatns';

import {
  isEditMode as isEditModeSelector,
  isMigrateMode as isMigrateModeSelector,
  alertsInfo as alertsInfoSelector,
  context as contextSelector,
  userTimeZone as userTimeZoneSelector,
  queryFactItem as queryFactItemSelector,
  description as descriptionSelector,
  alertUuid as alertUuidSelector,
  originalAlertName as originalAlertNameSelector,

  // form
  alertForm as alertFormSelector,
  alertFormEmails as alertFormEmailsSelector,
  alertFormProcessOptions as alertFormProcessOptionsSelector,
  alertFormDayOfWeekAvailable as alertFormDayOfWeekAvailableSelector,
  alertFormDayOfMonthAvailable as alertFormDayOfMonthAvailableSelector,

  // basic/advanced content databases
  alertFormDatabases as alertFormDatabasesSelector,
  basicContentDatabases as basicContentDatabasesSelector,
  advancedContentDatabase as advancedContentDatabaseSelector,

  // defaults
  defaultsTitleAndBiblio as defaultsTitleAndBiblioSelector,
  defaultsAbstract as defaultsAbstractSelector,
  defaultsFullAbstract as defaultsFullAbstractSelector,
  defaultsPartialAbstract as defaultsPartialAbstractSelector,
  defaultsHitDetails as defaultsHitDetailsSelector,

  // process options
  processOptionZeroResults as processOptionZeroResultsSelector,
  processOptionTitleAndBiblio as processOptionTitleAndBiblioSelector,
  processOptionAbstract as processOptionAbstractSelector,
  processOptionFullAbstract as processOptionFullAbstractSelector,
  processOptionPartialAbstract as processOptionPartialAbstractSelector,
  processOptionHitDetails as processOptionHitDetailsSelector,

  // trackingType for AA
  trackingType as trackingTypeSelector,

  // show non-owner email message
  showNonOwnerEmailMessage as showNonOwnerEmailMessageSelector,
} from './AlertCreateDialogSelectors';
import { getOption, isOptionChecked } from './processOptionsUtils';
import {
  validateEmptyInput as validateEmptyInputUtil,
  validateAlertNameLength as validateAlertNameLengthUtil,
  validateDuplicateAlertName as validateDuplicateAlertNameUtil,
  validateEmptyEmails as validateEmptyEmailsUtil,
  validateEmail as validateEmailUtil,
  combineValidators as combineValidatorsUtil,
} from './validationUtils';

/* eslint-disable no-unused-expressions */
/* eslint-disable no-param-reassign */
/* eslint-disable no-use-before-define */
export function open({ alert, databaseId, mode, beforeSuccess, onSuccess }) {
  return updateDialog({
    alert,
    databaseId,
    mode,
    beforeSuccess,
    onSuccess,

    id: typeOfAlertCreateDialog,
    dialogType: typeOfAlertCreateDialog,
    movable: false,
    backdrop: true,
  });
}

/* Dialog-related Actions */

export function dispatchOpen(...args) {
  return store.dispatch(open(...args));
}

export function mergeState(payload) {
  return {
    type: alertCreateDialogMergeStateReducerType,
    payload,
  };
}

/* Init promises */

async function _initAlertsInfo(dispatch, { context }) {
  const alertsInfo = await getAlertsInfoService({ context });

  dispatch(mergeState({ alertsInfo }));

  return alertsInfo;
}

async function _initQuery(dispatch, { databaseId, whereClause, queryFactItem, description, context }) {
  queryFactItem = queryFactItem || (await importAlertsWhereClauseService({ databaseId, whereClauseImportToken: whereClause }));
  description = description || defineAlertDescriptionFromFactItem(queryFactItem, context);
  const structures = defineAlertStructuresFromFactItem(queryFactItem);

  dispatch(
    mergeState({
      queryFactItem,
      description,
      structures: fromJS(structures),
    })
  );

  return queryFactItem;
}

export function resetAlertCreateDialog() {
  return {
    type: alertCreateDialogResetReducerType,
  };
}

/* Init action */

export const init = withGlobalSpinnerActionEnhancer(
  (
    {
      whereClause,
      alertName, // form
      alertUuid,
      allEmails, // form - emails
      allDatabases, // form - databases
      context,
      dayOfWeek, // form
      dayOfMonth, // form
      description,
      disableEditQuery,
      frequency, // form
      processOptions, // form
      queryFactItem,
      triggerMode, // form

      trackingType, // trackingType for AA
    },
    // for importing whereClause to get queryFactItem,
    // also is added to form.databases if present in alertsInfo.databases
    databaseId
  ) =>
    async (dispatch, getState) => {
      const userTimeZone = moment.tz.guess();
      const userEmail = userEmailSelector(getState());
      const emails = [userEmail].concat(without(allEmails, userEmail));

      const updatedState = {
        userTimeZone,
        context,
        ...(alertUuid && { alertUuid }),
        ...(disableEditQuery && { disableEditQuery }),
        ...(trackingType && { trackingType }),
        original: Map({
          ...(alertName && { alertName }),
          ...(allDatabases && { databases: allDatabases }),
        }),
        showNonOwnerEmailMessage: false,
      };

      const updatedForm = {
        emails,
        ...(alertName && { alertName }),
        ...(dayOfWeek && { dayOfWeek }),
        ...(dayOfMonth && { dayOfMonth: String(dayOfMonth) }), // TODO remove after BE changes
        ...(frequency && { frequency }),
        ...(processOptions && { processOptions }),
        ...(triggerMode && { triggerMode }),
      };

      dispatch(mergeState(updatedState));

      try {
        const [alertsInfo] = await Promise.all([
          _initAlertsInfo(dispatch, { context }),
          _initQuery(dispatch, { databaseId, whereClause, queryFactItem, description, context }),
        ]);

        // if during editing or migrating default database is unchecked - do not merge default processOptions
        const omitProcessOptions = allDatabases && !find(allDatabases, (db) => UserDatabases.isDefaultDatabase(db));
        const defaults = omit(alertsInfo.defaults, omitProcessOptions && ['processOptions']);

        // Set form.databases, based on whether allDatabases is provided:
        // 1. allDatabases provided (edit or migrate mode) - put them in
        // form.databases (only the ones that are also present in alertsInfo.databases)
        // 2. allDatabases is not provided (create mode) - put union of defaults.databases
        // and [databaseId] (only if it is also present in alertsInfo.databases)
        const databases = allDatabases
          ? intersection(allDatabases, alertsInfo.databases)
          : union(defaults.databases, intersection([databaseId], alertsInfo.databases));

        dispatch(
          mergeAlertForm({
            ...defaults,
            ...updatedForm,
            databases,
          })
        );
      } catch (e) {
        Logger.error(e);
      }
    }
);

/* Form validation Actions */

export function validateAlertName(alertName, ownProps) {
  return (dispatch, getState) => {
    const alertsInfo = alertsInfoSelector(getState());
    const originalAlertName = originalAlertNameSelector(getState());
    const isEditMode = isEditModeSelector(getState(), ownProps);

    return combineValidatorsUtil(
      validateEmptyInputUtil(alertName),
      validateAlertNameLengthUtil(alertName),
      validateDuplicateAlertNameUtil(alertName, alertsInfo.alerts, isEditMode && originalAlertName)
    )({ combineLogic: '&&' });
  };
}

function _containsOnlyOwnerEmail(values, ownerEmail) {
  const emails = map(values, 'value');
  return emails.length === 1 && includes(emails, ownerEmail);
}

export function validateEmailsInputOnChange(inputValue, values) {
  return (dispatch, getState) => {
    const state = getState();
    const showNonOwnerEmailMessage = showNonOwnerEmailMessageSelector(state);
    const userEmail = userEmailSelector(state);

    if (!showNonOwnerEmailMessage && inputValue) {
      dispatch(mergeState({ showNonOwnerEmailMessage: true }));
    }

    if (showNonOwnerEmailMessage && !inputValue && _containsOnlyOwnerEmail(values, userEmail)) {
      dispatch(mergeState({ showNonOwnerEmailMessage: false }));
    }

    return combineValidatorsUtil(validateEmptyInputUtil(inputValue), validateEmptyEmailsUtil(values))({ combineLogic: '||' });
  };
}

export function validateEmailsInputOnSelect(email) {
  return () => validateEmailUtil(email);
}

/* Update Alert form Actions */

export function mergeAlertForm(form) {
  return {
    type: alertCreateDialogMergeFormReducerType,
    payload: form,
  };
}

export function updateAlertForm(key, value) {
  return {
    type: alertCreateDialogUpdateFormReducerType,
    payload: {
      key,
      value,
    },
  };
}

export function setAlertFormAlertName(alertName) {
  return updateAlertForm('alertName', alertName);
}

export function addAlertFormEmail(email) {
  return (dispatch, getState) => {
    const alertFormEmails = alertFormEmailsSelector(getState());
    const updatedEmails = alertFormEmails.concat(email);

    return dispatch(updateAlertForm('emails', updatedEmails));
  };
}

export function removeAlertFormEmail(index) {
  return (dispatch, getState) => {
    const alertFormEmails = alertFormEmailsSelector(getState());
    const updatedEmails = alertFormEmails.filter((v, i) => i !== index);

    return dispatch(updateAlertForm('emails', updatedEmails));
  };
}

export function setAlertFormFrequency(frequency) {
  return updateAlertForm('frequency', frequency);
}

export function setAlertFormDayOfWeek(dayOfWeek) {
  return updateAlertForm('dayOfWeek', dayOfWeek);
}

export function setAlertFormDayOfMonth(dayOfMonth) {
  return updateAlertForm('dayOfMonth', dayOfMonth);
}

export function setAlertFormTriggerMode(triggerMode) {
  return updateAlertForm('triggerMode', triggerMode);
}

// basic/advanced content databases
export function toggleAlertFormDatabase(value) {
  return (dispatch, getState) => {
    let alertFormDatabases = alertFormDatabasesSelector(getState());

    if (isOptionChecked(value, alertFormDatabases)) {
      alertFormDatabases = without(alertFormDatabases, value);
    } else {
      alertFormDatabases = union(alertFormDatabases, [value]);
    }

    dispatch(updateAlertForm('databases', alertFormDatabases));
  };
}

export function toggleAdvancedContentDatabase() {
  return (dispatch, getState) => {
    const { value: advancedContentDatabaseValue, checked: advancedContentDatabaseChecked } = advancedContentDatabaseSelector(getState());
    const { value: titleAndBiblioValue } = processOptionTitleAndBiblioSelector(getState());
    const { value: abstractValue } = processOptionAbstractSelector(getState());
    const { value: fullAbstractValue } = processOptionFullAbstractSelector(getState());
    const { value: partialAbstractValue } = processOptionPartialAbstractSelector(getState());
    const { value: hitDetailsValue } = processOptionHitDetailsSelector(getState());

    const { value: defaultsTitleAndBiblioValue, checked: defaultsTitleAndBiblbioChecked } = defaultsTitleAndBiblioSelector(getState());
    const { value: defaultsAbstractValue, checked: defaultsAbstractChecked } = defaultsAbstractSelector(getState());
    const { value: defaultsFullAbstractValue, checked: defaultsFullAbstractChecked } = defaultsFullAbstractSelector(getState());
    const { value: defaultsPartialAbstractValue, checked: defaultsPartialAbstractChecked } = defaultsPartialAbstractSelector(getState());
    const { value: defaultsHitDetailsValue, checked: defaultsHitDetailsChecked } = defaultsHitDetailsSelector(getState());

    dispatch(toggleAlertFormDatabase(advancedContentDatabaseValue));

    if (advancedContentDatabaseChecked) {
      dispatch(
        updateAlertFormProcessOption({
          uncheckValues: [titleAndBiblioValue, abstractValue, fullAbstractValue, partialAbstractValue, hitDetailsValue],
        })
      );
    } else {
      const checkedDefaults = [
        ...(defaultsTitleAndBiblbioChecked ? [defaultsTitleAndBiblioValue] : []),
        ...(defaultsAbstractChecked ? [defaultsAbstractValue] : []),
        ...(defaultsFullAbstractChecked ? [defaultsFullAbstractValue] : []),
        ...(defaultsPartialAbstractChecked ? [defaultsPartialAbstractValue] : []),
        ...(defaultsHitDetailsChecked ? [defaultsHitDetailsValue] : []),
      ];

      dispatch(
        updateAlertFormProcessOption({
          checkValues: checkedDefaults,
        })
      );
    }
  };
}

// process options
export function updateAlertFormProcessOption({ checkValues = [], uncheckValues = [] } = {}) {
  return (dispatch, getState) => {
    let alertFormProcessOptions = alertFormProcessOptionsSelector(getState());
    alertFormProcessOptions = without(union(alertFormProcessOptions, checkValues), ...uncheckValues);
    dispatch(updateAlertForm('processOptions', alertFormProcessOptions));
  };
}

export function toggleProcessOptionZeroResults() {
  return (dispatch, getState) => {
    const { value: zeroResultsValue, checked: zeroResultsChecked } = processOptionZeroResultsSelector(getState());
    if (!zeroResultsChecked) {
      dispatch(
        updateAlertFormProcessOption({
          checkValues: [zeroResultsValue],
        })
      );
    } else {
      dispatch(
        updateAlertFormProcessOption({
          uncheckValues: [zeroResultsValue],
        })
      );
    }
  };
}

export function toggleProcessOptionTitleAndBiblio() {
  return (dispatch, getState) => {
    const { value: advancedContentDatabaseValue, checked: advancedContentDatabaseChecked } = advancedContentDatabaseSelector(getState());
    const { value: titleAndBiblioValue, checked: titleAndBiblioChecked } = processOptionTitleAndBiblioSelector(getState());

    if (!titleAndBiblioChecked) {
      dispatch(
        updateAlertFormProcessOption({
          checkValues: [titleAndBiblioValue],
        })
      );

      !advancedContentDatabaseChecked && dispatch(toggleAlertFormDatabase(advancedContentDatabaseValue));
    } else {
      dispatch(
        updateAlertFormProcessOption({
          uncheckValues: [titleAndBiblioValue],
        })
      );
    }
  };
}

export function toggleProcessOptionAbstract() {
  return (dispatch, getState) => {
    const { value: advancedContentDatabaseValue, checked: advancedContentDatabaseChecked } = advancedContentDatabaseSelector(getState());
    const { value: titleAndBiblioValue } = processOptionTitleAndBiblioSelector(getState());
    const { value: abstractValue, checked: abstractChecked } = processOptionAbstractSelector(getState());
    const { value: fullAbstractValue } = processOptionFullAbstractSelector(getState());
    const { value: partialAbstractValue } = processOptionPartialAbstractSelector(getState());
    const { value: defaultsFullAbstractValue, checked: defaultsFullAbstractChecked } = defaultsFullAbstractSelector(getState());
    const { value: defaultsPartialAbstractValue, checked: defaultsPartialAbstractChecked } = defaultsPartialAbstractSelector(getState());

    if (!abstractChecked) {
      dispatch(
        updateAlertFormProcessOption({
          checkValues: [
            titleAndBiblioValue,
            abstractValue,
            ...(defaultsFullAbstractChecked ? [defaultsFullAbstractValue] : []),
            ...(defaultsPartialAbstractChecked ? [defaultsPartialAbstractValue] : []),
          ],
        })
      );

      !advancedContentDatabaseChecked && dispatch(toggleAlertFormDatabase(advancedContentDatabaseValue));
    } else {
      dispatch(
        updateAlertFormProcessOption({
          uncheckValues: [abstractValue, fullAbstractValue, partialAbstractValue],
        })
      );
    }
  };
}

export function setProcessOptionFullAbstract() {
  return (dispatch, getState) => {
    const { value: advancedContentDatabaseValue, checked: advancedContentDatabaseChecked } = advancedContentDatabaseSelector(getState());
    const { value: titleAndBiblioValue } = processOptionTitleAndBiblioSelector(getState());
    const { value: abstractValue } = processOptionAbstractSelector(getState());
    const { value: fullAbstractValue } = processOptionFullAbstractSelector(getState());
    const { value: partialAbstractValue } = processOptionPartialAbstractSelector(getState());

    dispatch(
      updateAlertFormProcessOption({
        checkValues: [titleAndBiblioValue, abstractValue, fullAbstractValue],
        uncheckValues: [partialAbstractValue],
      })
    );

    !advancedContentDatabaseChecked && dispatch(toggleAlertFormDatabase(advancedContentDatabaseValue));
  };
}

export function setProcessOptionPartialAbstract() {
  return (dispatch, getState) => {
    const { value: advancedContentDatabaseValue, checked: advancedContentDatabaseChecked } = advancedContentDatabaseSelector(getState());
    const { value: titleAndBiblioValue } = processOptionTitleAndBiblioSelector(getState());
    const { value: abstractValue } = processOptionAbstractSelector(getState());
    const { value: partialAbstractValue } = processOptionPartialAbstractSelector(getState());
    const { value: fullAbstractValue } = processOptionFullAbstractSelector(getState());

    dispatch(
      updateAlertFormProcessOption({
        checkValues: [titleAndBiblioValue, abstractValue, partialAbstractValue],
        uncheckValues: [fullAbstractValue],
      })
    );

    !advancedContentDatabaseChecked && dispatch(toggleAlertFormDatabase(advancedContentDatabaseValue));
  };
}

export function toggleProcessOptionHitDetails() {
  return (dispatch, getState) => {
    const { value: advancedContentDatabaseValue, checked: advancedContentDatabaseChecked } = advancedContentDatabaseSelector(getState());
    const { value: hitDetailsValue, checked: hitDetailsChecked } = processOptionHitDetailsSelector(getState());

    if (!hitDetailsChecked) {
      dispatch(
        updateAlertFormProcessOption({
          checkValues: [hitDetailsValue],
        })
      );

      !advancedContentDatabaseChecked && dispatch(toggleAlertFormDatabase(advancedContentDatabaseValue));
    } else {
      dispatch(
        updateAlertFormProcessOption({
          uncheckValues: [hitDetailsValue],
        })
      );
    }
  };
}

// Submit create/edit stuff
export function _closeShowSuccessAndReload(dispatch, successProps) {
  // close current
  dispatch(closeDialog({ id: typeOfAlertCreateDialog }));

  // open success
  dispatch(
    updateDialog({
      // props-ы
      ...successProps,

      // dialog-related stuff
      id: typeOfAlertCreateSuccessDialog,
      dialogType: typeOfAlertCreateSuccessDialog,
      movable: false,
      backdrop: true,
    })
  );

  // reset alerts in store - so it will re-fetch them on next alerts page visit
  dispatch(resetAlerts());
}

export const submitAlertForm = withGlobalSpinnerActionEnhancer((ownProps) => async (dispatch, getState) => {
  const isEditMode = isEditModeSelector(getState(), ownProps);
  const isMigrateMode = isMigrateModeSelector(getState(), ownProps);

  const { beforeSuccess, onSuccess } = ownProps;

  const alertFormDayOfWeekAvailable = alertFormDayOfWeekAvailableSelector(getState());
  const alertFormDayOfMonthAvailable = alertFormDayOfMonthAvailableSelector(getState());

  const alertsInfo = alertsInfoSelector(getState());
  const immutableForm = alertFormSelector(getState());
  const form =
    immutableForm &&
    immutableForm
      .filter((v, k) => !(k === 'dayOfWeek' && !alertFormDayOfWeekAvailable) && !(k === 'dayOfMonth' && !alertFormDayOfMonthAvailable))
      .toJS();

  const context = contextSelector(getState());
  const userTimeZone = userTimeZoneSelector(getState());
  const queryFactItem = queryFactItemSelector(getState());
  const description = descriptionSelector(getState());
  const uuid = alertUuidSelector(getState());
  const trackingType = trackingTypeSelector(getState());

  const requestParams = {
    ...(uuid && { uuid }),
    context,
    userTimeZone,
    queryFactItem,
    description,
    ...form,
  };

  const successProps = {
    query: description,
    alertName: form.alertName,
    emails: form.emails,
    frequency: getOption(form.frequency, alertsInfo.frequencies).label,
    ...(alertFormDayOfWeekAvailable && { dayOfWeek: getOption(form.dayOfWeek, alertsInfo.daysOfWeek).label }),
    ...(alertFormDayOfMonthAvailable && { dayOfMonth: getOption(form.dayOfMonth, alertsInfo.daysOfMonth).label }),
    processOptions: form.processOptions.map((option) => getOption(option, alertsInfo.processOptions).label),
    databases: form.databases.map((db) => UserDatabases.getDbName(db)),
  };

  // AA
  Catalyst.trackSavedAlert({
    context,
    description,
    trackingType,
    frequency: defineAlertFrequency(successProps),
    details: defineAlertDetails({
      databases: form.databases,
      processOptions: form.processOptions,
      basicContentDatabases: basicContentDatabasesSelector(getState()),
      advancedContentDatabase: advancedContentDatabaseSelector(getState()),
    }),
  });

  if (isEditMode) {
    // edit alert
    try {
      await updateAlertService(requestParams);
      beforeSuccess && (await beforeSuccess());
      _closeShowSuccessAndReload(dispatch, { ...successProps, headerTitle: 'Your alert has been successfully edited!' });
      onSuccess && onSuccess();
    } catch (e) {
      Logger.error(e);
    }
  } else if (isMigrateMode) {
    // migrate alert - create the new one amd delete the old ones
    try {
      await createAlertService(requestParams);
      beforeSuccess && (await beforeSuccess());
      _closeShowSuccessAndReload(dispatch, { ...successProps, headerTitle: 'Your alert is successfully migrated!' });
      onSuccess && onSuccess();
    } catch (e) {
      Logger.error(e);
    }
  } else {
    // create alert
    try {
      await createAlertService(requestParams);
      beforeSuccess && (await beforeSuccess());
      _closeShowSuccessAndReload(dispatch, { ...successProps, headerTitle: 'Your alert is successfully created!' });
      onSuccess && onSuccess();
    } catch (e) {
      Logger.error(e);
    }
  }
});

export function dispatchCreateAlert(crumb) {
  const defaultAlert = {
    alertName: 'alert1', // by default, the alert name field should be repopulated with "alert 1"
    frequency: 'after each update', // by default "After each update" is selected
    emails: [],
    databases: [],
  };
  const alert = extend(defaultAlert, pick(crumb, ['context', 'databaseId', 'queryFactItem', 'quickSearchItem', 'trackingType']));
  alert.description = crumb.comment || crumb.description;

  // but check for Anon user should be here to avoid init of Alerts Create modal
  if (isAnonymousUser()) {
    const errorMessage = 'Please sign-in/register to create an Alert.';
    const errorTitle = 'Login required';
    Catalyst.trackError({
      message: `${errorTitle} ${errorMessage}`,
      component: 'Alerts',
    });
    return store.dispatch(
      updateDialog({
        id: typeOfAlertLoginDialog,
        dialogType: typeOfAlertLoginDialog,
        alert,
      })
    );
  }

  return dispatchOpen({
    alert: {
      context: alert.context,
      description: alert.description,
      queryFactItem: alert.queryFactItem,
      whereClause: get(alert, 'quickSearchItem.whereClauseImportToken'),
      trackingType: alert.trackingType,
    },
    mode: alertDialogModes.create,
    databaseId: alert.databaseId,
  });
}
